<template>
  <SoTooltip v-if="shouldShowTooltip" :text="disabledTooltip">
    <SoButton
      :title="buttonText"
      large
      disabled
      data-test-id="export-csv-button"
    />
  </SoTooltip>

  <SoButton
    v-else
    :title="buttonText"
    large
    :loading="data.isLoading"
    data-test-id="export-csv-button"
    @click="exportData"
  />
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { unparse } from "papaparse"
  import {
    SoDataTableColumnType,
    SoDataTableHeader,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import { formatDate } from "@/helpers/dateHelper"
  import { downloadFile } from "@/helpers/downloadFile"

  export type SoExportCsvButtonData = {
    isLoading: boolean
    items: SoDataTableItem[]
  }

  type MappedLabels = {
    label: string
    type: SoDataTableColumnType
  }

  export default defineComponent({
    components: {
      SoTooltip,
      SoButton,
    },
    props: {
      disabled: {
        type: Boolean,
        default: false,
        required: false,
      },
      disabledTooltip: {
        type: String,
        default: null,
        required: false,
      },
      encoding: {
        type: String,
        default: "utf-8",
        required: false,
      },
      fileName: {
        type: String,
        default: "export.csv",
        required: false,
      },
      data: {
        type: Object as PropType<SoExportCsvButtonData>,
        required: true,
        default: () => ({
          isLoading: false,
          items: [],
        }),
      },
      labels: {
        type: Array as PropType<SoDataTableHeader[]>,
        required: false,
        default: () => [],
      },
      buttonText: {
        type: String,
        default: "Export CSV",
        required: false,
      },
      asyncDownload: {
        type: Boolean,
        default: true,
        required: false,
      },
    },
    emits: ["export"],
    computed: {
      shouldShowTooltip() {
        return this.disabledTooltip && this.disabled
      },
    },
    watch: {
      data: {
        deep: true,
        handler(newData) {
          if (!this.asyncDownload) return
          if (!newData.isLoading && newData.items.length) {
            this.generateAndSaveCsv()
          }
        },
      },
    },
    methods: {
      exportData() {
        this.$emit("export")

        if (!this.asyncDownload) {
          this.generateAndSaveCsv()
        }
      },
      generateAndSaveCsv() {
        const mappedData = this.labels
          ? this.mapItemsToLabels(this.data.items, this.labels)
          : this.data.items
        const csv = unparse(mappedData, {
          delimiter: ",",
          encoding: "utf-8",
        })

        downloadFile(csv, this.fileName, `text/csv;charset=${this.encoding}`)
      },
      mapItemsToLabels(items, labels) {
        const labelsMap: MappedLabels[] = labels.reduce(
          (acc, label) => ({
            ...acc,
            [label.key]: { label: label.title, type: label.type },
          }),
          {},
        )

        return items.map((item) => {
          return Object.entries(labelsMap).reduce(
            (newItem, [labelKey, labelValue]) => {
              let parsedValue: string
              const itemFieldValue = item[labelKey]
              // add mappings if necessary, details in SoDataTableCell.vue
              switch (labelValue.type) {
                case SoDataTableColumnType.DATE:
                  parsedValue = itemFieldValue ? formatDate(itemFieldValue) : ""
                  break
                case SoDataTableColumnType.STATUS:
                  parsedValue = itemFieldValue.value
                  break
                default:
                  parsedValue = itemFieldValue
              }
              return { ...newItem, [labelValue.label]: parsedValue }
            },
            {},
          )
        })
      },
    },
  })
</script>
